<template>
  <div class="page">
    <header class="hero fade-in">
      <h1>Stud + Metal Scanner Support</h1>
      <p>
        Help using the app, troubleshooting issues, or sending feedback —
        everything you need in one place.
      </p>
    </header>

    <!-- HOW TO USE -->
    <section class="card fade-in">
      <h2>How to Use</h2>

      <div class="usage">
        <div class="usage-item">
          <h3>🧱 Stud Finder</h3>
          <ul>
            <li>Hold the bottom third of your iPhone near the wall</li>
            <li>Move slowly to locate studs, screws, nails, and metal</li>
          </ul>
        </div>

        <div class="usage-item">
          <h3>🧲 Magnetometer (EMF)</h3>
          <ul>
            <li>Switch to Magnetometer mode</li>
            <li>View real-time magnetic field trends</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- FAQ ACCORDION -->
    <section class="card fade-in">
      <h2>FAQs</h2>

      <div
        v-for="(faq, index) in faqs"
        :key="index"
        class="faq-item"
      >
        <button class="faq-question" @click="toggle(index)">
          <span class="icon">❓</span>
          {{ faq.q }}
          <span class="chevron">{{ active === index ? '⌄' : '›' }}</span>
        </button>

        <transition name="accordion">
          <div v-if="active === index" class="faq-answer">
            {{ faq.a }}
          </div>
        </transition>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="card contact fade-in">
      <h2>Contact Support</h2>
      <p>
        Have feedback or found a bug?<br />
        <a href="mailto:care@thewishboard.com">care@thewishboard.com</a>
      </p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'StudFinderSupport',
  data() {
    return {
      active: null,
      faqs: [
        {
          q: 'Why does the app need calibration?',
          a: 'Calibration adjusts the magnetometer to your environment, filtering interference for better accuracy.'
        },
        {
          q: 'How do I calibrate the app?',
          a: 'Move your phone in a gentle figure-eight motion for a few seconds until calibration completes.'
        },
        {
          q: 'Why can’t I find any studs?',
          a: 'Move slowly and keep the phone flat. Thick walls or plaster may reduce detection accuracy.'
        },
        {
          q: 'Does the app need internet?',
          a: 'No. The app works completely offline.'
        },
        {
          q: 'What materials can it detect?',
          a: 'Wood and metal studs, pipes, wiring, screws, and nails behind drywall.'
        },
        {
          q: 'Why am I seeing false readings?',
          a: 'Recalibrate the app and avoid nearby electronics or metal objects.'
        },
        {
          q: 'Should I remove my phone case?',
          a: 'Thick or metallic cases can interfere with readings. Removing them improves accuracy.'
        }
      ]
    }
  },
  methods: {
    toggle(index) {
      this.active = this.active === index ? null : index
    }
  }
}
</script>

<style scoped>
/* ===== Base ===== */
.page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: var(--bg);
  color: var(--text);
  padding: 24px;
  min-height: 100vh;
}

/* ===== Colors (Light) ===== */
:root {
  --bg: #f5f6f8;
  --card: #ffffff;
  --text: #1c1c1e;
  --subtext: #5a5a5f;
  --accent: #007aff;
  --border: rgba(0, 0, 0, 0.08);
}

/* ===== Dark Mode ===== */
@media (prefers-color-scheme: dark) {
  :root {
    --bg: #000;
    --card: #1c1c1e;
    --text: #f5f5f7;
    --subtext: #a1a1a6;
    --accent: #0a84ff;
    --border: rgba(255, 255, 255, 0.12);
  }
}

/* ===== Hero ===== */
.hero {
  max-width: 820px;
  margin: 0 auto 32px;
  text-align: center;
}

.hero h1 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.hero p {
  color: var(--subtext);
}

/* ===== Card ===== */
.card {
  max-width: 820px;
  margin: 0 auto 24px;
  background: var(--card);
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

h2 {
  font-size: 1.4rem;
  margin-bottom: 16px;
}

h3 {
  font-size: 1.1rem;
  margin-bottom: 6px;
}

/* ===== Usage ===== */
.usage {
  display: grid;
  gap: 20px;
}

.usage-item ul {
  padding-left: 18px;
}

.usage-item li {
  margin-bottom: 6px;
  color: var(--subtext);
}

/* ===== FAQ Accordion ===== */
.faq-item {
  border-bottom: 1px solid var(--border);
}

.faq-question {
  width: 100%;
  background: none;
  border: none;
  padding: 16px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-align: left;
}

.faq-question:hover {
  color: var(--accent);
}

.icon {
  font-size: 1.1rem;
}

.chevron {
  margin-left: auto;
  font-size: 1.2rem;
  opacity: 0.6;
}

.faq-answer {
  padding: 0 0 16px 28px;
  color: var(--subtext);
  line-height: 1.5;
}

/* ===== Accordion Animation ===== */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.25s ease;
}

.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ===== Contact ===== */
.contact {
  text-align: center;
}

.contact a {
  color: var(--accent);
  font-weight: 600;
  text-decoration: none;
}

.contact a:hover {
  text-decoration: underline;
}

/* ===== Fade-in ===== */
.fade-in {
  animation: fadeIn 0.6s ease both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
